/**
 * Hook that returns whether the current user has at least one strategy.
 * Shares the same React Query cache as useUserStrategies.
 * Polls every 5 s while the list is empty; stops once a strategy exists.
 */

import { useQuery } from '@tanstack/react-query';
import { getUserStrategies } from '../api/client';
import { userStrategyKeys } from './useUserStrategies';
import type { UserStrategy } from '../types';

export interface UseHasStrategiesResult {
  hasStrategies: boolean;
  isLoading: boolean;
}

export function useHasStrategies(): UseHasStrategiesResult {
  const { data, isLoading } = useQuery<UserStrategy[], Error>({
    queryKey: userStrategyKeys.all,
    queryFn: getUserStrategies,
    staleTime: 60_000,
    // Poll every 5 s while the list is empty; stop once ≥1 strategy exists.
    refetchInterval: (query) => {
      const count = (query.state.data?.length ?? 0);
      return count === 0 ? 5000 : false;
    },
  });

  return {
    hasStrategies: (data?.length ?? 0) > 0,
    isLoading,
  };
}
